import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Gmap from './Gmap';
import Select from 'react-select';
import Slider from '@mui/material/Slider';
import {
  homeTypeOptions,
  unitTypeOptions,
  propertyFeaturesOptions,
  rentalPropertyFeaturesOptions,
} from '../contants/data';

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const toInputDate = (date) => {
  // yyyy-mm-dd for <input type="date">
  const d = new Date(date);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const Filters = ({ onFilter, data, stateCategory, filters }) => {
  const [thunks, setThunks] = useState(null);
  const [showAdvFilter, setShowAdvFilter] = useState(false);
  const { isFilter } = useSelector((state) => state[stateCategory]);
  const addressVal = useSelector(
    (state) => state[stateCategory].filters.placeName
  );
  const dispatch = useDispatch();

  // GEOLOCATE
  const { lat, lng, radius, placeName } = useSelector(
    (state) => state[stateCategory].filters.location
  );

  // NEW LAT,LNG
  const [newLat, setNewLat] = useState('');
  const [newLng, setNewLng] = useState('');
  const [newPlaceName, setNewPlaceName] = useState('');

  // PRICE
  const { min: priceMin, max: priceMax } = useSelector(
    (state) => state[stateCategory].filters.price_range
  );
  const [priceVal, setPriceVal] = useState([priceMin, priceMax]);
  const handlePriceChange = (event, newValue) => {
    setPriceVal(newValue);
    dispatch(thunks?.changePrice({ min: newValue[0], max: newValue[1] }));
  };
  const handleMinPriceChange = (e) => {
    const newMin = Number(e.target.value);
    setPriceVal([newMin, priceVal[1]]);
    dispatch(thunks?.changePrice({ min: newMin, max: priceVal[1] }));
  };
  const handleMaxPriceChange = (e) => {
    const newMax = Number(e.target.value);
    setPriceVal([priceVal[0], newMax]);
    dispatch(thunks?.changePrice({ min: priceVal[0], max: newMax }));
  };

  // BEDS
  const { min: bedsMin, max: bedsMax } = useSelector(
    (state) => state[stateCategory].filters.beds_range
  );
  const [bedsVal, setBedsVal] = useState([bedsMin, bedsMax]);
  const handleBedsChange = (event, newValue) => {
    setBedsVal(newValue);
    dispatch(thunks?.changeBeds({ min: newValue[0], max: newValue[1] }));
  };

  // SLEEPS
  const { min: sleepsMin, max: sleepsMax } = useSelector(
    (state) => state[stateCategory].filters.sleeps_range
  );
  const [sleepsVal, setSleepsVal] = useState([sleepsMin, sleepsMax]);
  const handleSleepsChange = (event, newValue) => {
    setSleepsVal(newValue);
    dispatch(thunks?.changeSleeps({ min: newValue[0], max: newValue[1] }));
  };

  // BATHS
  const { min: bathsMin, max: bathsMax } = useSelector(
    (state) => state[stateCategory].filters.baths_range
  );
  const [bathsVal, setBathsVal] = useState([bathsMin, bathsMax]);
  const handleBathsChange = (event, newValue) => {
    setBathsVal(newValue);
    dispatch(thunks?.changeBaths({ min: newValue[0], max: newValue[1] }));
  };

  // SIZE SQFT
  const { min: sizeSqFtMin, max: sizeSqFtMax } = useSelector(
    (state) => state[stateCategory].filters.size_sqft_range
  );
  const [sizeSqFtVal, setSizeSqFtVal] = useState([sizeSqFtMin, sizeSqFtMax]);
  const handleSizeSqFtChange = (event, newValue) => {
    setSizeSqFtVal(newValue);
    dispatch(thunks?.changeSizeSqFt({ min: newValue[0], max: newValue[1] }));
  };

  // --- Dates (limit past, defaults 7 days ahead) ---
  const storeCheckin = useSelector(
    (state) => state[stateCategory].filters.checkin_date
  );
  const storeCheckout = useSelector(
    (state) => state[stateCategory].filters.checkout_date
  );

  const todayStr = toInputDate(new Date());
  const defaultCheckinStr = toInputDate(addDays(new Date(), 7));
  const defaultCheckoutStr = toInputDate(addDays(new Date(), 8));

  const [checkinDate, setCheckinDate] = useState(
    storeCheckin || defaultCheckinStr
  );
  const [checkoutDate, setCheckoutDate] = useState(
    storeCheckout || defaultCheckoutStr
  );

  // When thunks load, push defaults to store if empty
  useEffect(() => {
    if (!thunks) return;
    if (!storeCheckin) {
      dispatch(thunks?.changeCheckinDate(defaultCheckinStr));
    }
    if (!storeCheckout) {
      dispatch(thunks?.changeCheckoutDate(defaultCheckoutStr));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thunks]);

  // Keep checkout >= checkin
  useEffect(() => {
    if (checkoutDate < checkinDate) {
      const next = toInputDate(addDays(checkinDate, 1));
      setCheckoutDate(next);
    }
  }, [checkinDate, checkoutDate]);

  // HOME TYPE
  const { homeType } = useSelector((state) => state[stateCategory].filters);
  const [selectedHomeType, setSelectedHomeType] = useState([]);
  const handleHomeTypeChange = (options) => {
    setSelectedHomeType(options ? options.map((option) => option.value) : []);
    dispatch(
      thunks?.changeHomeType(
        options ? options.map((option) => option.value) : []
      )
    );
  };

  // UNIT TYPE
  const { unitType } = useSelector((state) => state[stateCategory].filters);
  const [selectedUnitType, setSelectedUnitType] = useState('');
  const handleUnitTypeChange = (option) => {
    setSelectedUnitType(option);
    dispatch(thunks?.changeUnitType(option));
  };

  // PROPERTY FEATURES
  const { propertyFeatures } = useSelector(
    (state) => state[stateCategory].filters
  );
  const [selectedPropertyFeatures, setSelectedPropertyFeatures] = useState([]);
  const handlePropertyFeaturesChange = (options) => {
    setSelectedPropertyFeatures(
      options ? options.map((option) => option.value) : []
    );
    dispatch(
      thunks?.changePropertyFeatures(
        options ? options.map((option) => option.value) : []
      )
    );
  };

  // RENTAL PROPERTY FEATURES
  const { rentalPropertyFeatures } = useSelector(
    (state) => state[stateCategory].filters
  );
  const [selectedRentalPropertyFeatures, setSelectedRentalPropertyFeatures] =
    useState([]);
  const handleRentalPropertyFeaturesChange = (options) => {
    setSelectedRentalPropertyFeatures(
      options ? options.map((option) => option.value) : []
    );
    dispatch(
      thunks?.changeRentalPropertyFeatures(
        options ? options.map((option) => option.value) : []
      )
    );
  };

  useEffect(() => {
    const loadThunks = async () => {
      let importedThunks;
      if (stateCategory === 'shortTermHousingRentals') {
        importedThunks = await import(
          '../store/slices/shortTermHousingRentals.slice'
        );
      }
      setThunks(importedThunks);
    };
    loadThunks();
  }, [stateCategory]);

  if (!thunks) {
    return <div>Loading...</div>;
  }

  const handleSubmit = () => {
    let finalLat = '';
    let finalLng = '';
    let finalPlaceName = '';
    if ((newLat != '') & (newLng != '')) {
      finalLat = newLat;
      finalLng = newLng;
      finalPlaceName = newPlaceName;
      dispatch(
        thunks?.changeLocation({ lat: finalLat, lng: finalLng, finalPlaceName })
      );
    } else {
      finalLat = lat;
      finalLng = lng;
      finalPlaceName = placeName;
    }

    onFilter({
      finalLat,
      finalLng,
      addressVal,
      radius,
      priceVal,
      bedsVal,
      sleepsVal,
      bathsVal,
      sizeSqFtVal,
      homeType,
      unitType,
      checkinDate,
      checkoutDate,
      propertyFeatures,
      rentalPropertyFeatures,
    });
    dispatch(thunks?.activateFilter(true));
    dispatch(thunks?.setSearchTerm(''));
    dispatch(thunks?.setSortBy('Closest to Me'));
    dispatch(thunks?.resetSortedData());
  };

  const updateLoc = (lat, lng, placeName) => {
    setNewLat(lat);
    setNewLng(lng);
    setNewPlaceName(placeName);
    dispatch(thunks?.changeAddress(placeName));
  };

  const handleResetFilter = () => {
    window.location.reload();
  };

  return (
    <div className='sidebar hide-mobile'>
      <Gmap onChangeLoc={updateLoc} data={data} />

      {filters.includes('Price') && (
        <div className='form-control-wrap'>
          <label>Price Per Night</label>
          <div className='range-inputs'>
            <input
              type='number'
              value={priceVal[0]}
              min={0}
              onChange={handleMinPriceChange}
              placeholder='Min'
            />
            {/* <span className='price-separator'>–</span>
            <input
              type='number'
              value={priceVal[1]}
              min={0}
              onChange={handleMaxPriceChange}
              placeholder='Max'
            /> */}
          </div>
        </div>
      )}

      <div className='field-cols-2'>
        {filters.includes('Check-in Date') && (
          <div className='form-control-wrap'>
            <label>Check-in Date</label>
            <input
              type='date'
              value={checkinDate}
              min={todayStr} // 🔒 cannot pick a past date
              onChange={(e) => {
                const next = e.target.value || todayStr;
                setCheckinDate(next);
                // Optional: push to store if you keep store in sync live
                dispatch(thunks?.changeCheckinDate(next));
                // If checkout is before new checkin, bump it by +1 day
                if (checkoutDate < next) {
                  const bump = toInputDate(addDays(next, 1));
                  setCheckoutDate(bump);
                  dispatch(thunks?.changeCheckoutDate(bump));
                }
              }}
            />
          </div>
        )}

        {filters.includes('Check-out Date') && (
          <div className='form-control-wrap'>
            <label>Check-out Date</label>
            <input
              type='date'
              value={checkoutDate}
              min={checkinDate || todayStr} // 🔒 cannot be before check-in
              onChange={(e) => {
                const next = e.target.value;
                setCheckoutDate(next);
                dispatch(thunks?.changeCheckoutDate(next));
              }}
            />
          </div>
        )}
      </div>

      <div className='field-cols-2'>
        {filters.includes('Beds') && (
          <div className='form-control-wrap'>
            <label>Min Beds</label>
            <div className='range-inputs'>
              <input
                type='number'
                value={bedsVal[0]}
                min={0}
                onChange={(e) => {
                  const newMin = Number(e.target.value);
                  setBedsVal([newMin, bedsVal[1]]);
                  dispatch(
                    thunks?.changeBeds({ min: newMin, max: bedsVal[1] })
                  );
                }}
                placeholder='Min'
              />
              {/* Max input omitted by design
              <span className='price-separator'>–</span>
              <input ... /> */}
            </div>
          </div>
        )}

        {filters.includes('Baths') && (
          <div className='form-control-wrap'>
            <label>Min Baths</label>
            <div className='range-inputs'>
              <input
                type='number'
                value={bathsVal[0]}
                min={0}
                onChange={(e) => {
                  const newMin = Number(e.target.value);
                  setBathsVal([newMin, bathsVal[1]]);
                  dispatch(
                    thunks?.changeBaths({ min: newMin, max: bathsVal[1] })
                  );
                }}
                placeholder='Min'
              />
              {/* Max input omitted by design */}
            </div>
          </div>
        )}
      </div>

      {/* Sleeps & other optional controls kept as-is */}

      {filters.includes('Size_sqft') && (
        <div className='form-control-wrap'>
          <label>Size (SQ. FT.)</label>
          <div className='range-inputs'>
            <input
              type='number'
              value={sizeSqFtVal[0]}
              min={0}
              onChange={(e) => {
                const newMin = Number(e.target.value);
                setSizeSqFtVal([newMin, sizeSqFtVal[1]]);
                dispatch(
                  thunks?.changeSizeSqFt({ min: newMin, max: sizeSqFtVal[1] })
                );
              }}
              placeholder='Min'
            />
            {/* Max input omitted by design */}
          </div>
        </div>
      )}

      {filters.includes('Home Type') && (
        <div className='form-control-wrap'>
          <label>Home Type</label>
          <Select
            isMulti
            options={homeTypeOptions}
            onChange={handleHomeTypeChange}
            value={homeTypeOptions.filter((option) =>
              selectedHomeType.includes(option.value)
            )}
            className='border-0'
          />
        </div>
      )}

      {filters.includes('Unit Type') && (
        <div className='form-control-wrap'>
          <label>Unit Type</label>
          <Select
            options={unitTypeOptions}
            onChange={(option) => handleUnitTypeChange(option?.value)}
            value={unitTypeOptions.find(
              (option) => option.value === selectedUnitType
            )}
            className='border-0'
          />
        </div>
      )}

      {filters.includes('Property Features') && (
        <div className='form-control-wrap'>
          <label>Property Features</label>
          <Select
            isMulti
            options={propertyFeaturesOptions}
            onChange={handlePropertyFeaturesChange}
            value={propertyFeaturesOptions.filter((option) =>
              selectedPropertyFeatures.includes(option.value)
            )}
            className='border-0'
          />
        </div>
      )}

      {filters.includes('Rental Property Features') && (
        <div className='form-control-wrap'>
          <label>Rental Property Features</label>
          <Select
            isMulti
            options={rentalPropertyFeaturesOptions}
            onChange={handleRentalPropertyFeaturesChange}
            value={rentalPropertyFeaturesOptions.filter((option) =>
              selectedRentalPropertyFeatures.includes(option.value)
            )}
            className='border-0'
          />
        </div>
      )}

      <button onClick={handleSubmit} className='primary mb-10'>
        Filter
      </button>
      <button
        onClick={handleResetFilter}
        className='reset'
        disabled={!isFilter}
      >
        Reset Filter
      </button>
    </div>
  );
};

export default Filters;
